((global) => { "use strict";
  const ATTRIBUTION_VERSION = "v2";
  const STORAGE_KEY = "kixbox_outfit_pending_v2";
  const PENDING_TTL_MS = 30 * 60 * 1000;
  const MAX_CART_ATTRIBUTE_ATTEMPTS = 2;
  const CART_ATTRIBUTES = Object.freeze({
    __outfit_used: "true",
    __outfit_attribution_version: ATTRIBUTION_VERSION,
  });
  const stringValue = (value) => (value == null ? "" : String(value));
  function createPendingContext(input, now = Date.now()) {
    return {
      version: ATTRIBUTION_VERSION,
      sourceProductId: stringValue(input.sourceProductId),
      outfitProductId: stringValue(input.outfitProductId),
      outfitId: stringValue(input.outfitId),
      outfitKind: stringValue(input.outfitKind),
      outfitSurface: stringValue(input.outfitSurface),
      outfitStyle: stringValue(input.outfitStyle),
      targetProductId: stringValue(input.targetProductId),
      capturedAt: now,
      expiresAt: now + PENDING_TTL_MS,
    };
  }
  function pendingContextIsWellFormed(context, now = Date.now()) {
    return Boolean(
      context &&
        context.version === ATTRIBUTION_VERSION &&
        context.sourceProductId &&
        context.outfitId &&
        context.outfitKind &&
        context.outfitSurface &&
        context.targetProductId &&
        Number.isFinite(context.capturedAt) &&
        Number.isFinite(context.expiresAt) &&
      context.expiresAt > now,
    );
  }
  function isPendingContextValid(context, currentProductId, now = Date.now()) {
    return pendingContextIsWellFormed(context, now) &&
      stringValue(context.targetProductId) === stringValue(currentProductId);
  }
  const shouldInterceptNativeAdd = (hasCartSurface, contextValid) => !hasCartSurface && contextValid;
  function lineProperties(context) {
    return {
      _outfit_source_product_id: stringValue(context.sourceProductId), _outfit_id: stringValue(context.outfitId),
      _outfit_kind: stringValue(context.outfitKind), _outfit_surface: stringValue(context.outfitSurface),
    };
  }
  function buildAttributedItem(input, context) {
    const item = { id: stringValue(input.variantId),
      quantity: Math.max(1, Number.parseInt(input.quantity, 10) || 1),
      properties: lineProperties(context) };
    if (input.sellingPlan) item.selling_plan = stringValue(input.sellingPlan);
    return item;
  }
  function addedItemHasAttribution(result, variantId, context) {
    const expected = lineProperties(context);
    return Boolean(result?.items?.some((row) =>
      stringValue(row.variant_id || row.id) === stringValue(variantId) &&
      Object.entries(expected).every(([key, value]) => stringValue(row.properties?.[key]) === value)));
  }
  const api = { ATTRIBUTION_VERSION, CART_ATTRIBUTES, MAX_CART_ATTRIBUTE_ATTEMPTS,
    PENDING_TTL_MS, addedItemHasAttribution, buildAttributedItem, createPendingContext,
    isPendingContextValid, lineProperties, shouldInterceptNativeAdd };
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  if (!global || !global.document) return;
  const document = global.document;
  const state = { page: null, pending: null, submittedVariantId: null, subscribed: false };
  function pageContext() {
    if (state.page) return state.page;
    const node = document.querySelector("[data-outfit-page-context]");
    if (!node) return null;
    try {
      state.page = JSON.parse(node.textContent || "{}");
      state.page.productId = stringValue(state.page.productId);
      return state.page;
    } catch (_error) {
      return null;
    }
  }
  function readPendingContext() {
    try {
      const raw = global.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const context = JSON.parse(raw);
      if (pendingContextIsWellFormed(context)) return context;
      global.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      try { global.sessionStorage.removeItem(STORAGE_KEY); } catch (_storageError) {}
    }
    return null;
  }
  function clearPendingContext() {
    state.pending = null;
    state.submittedVariantId = null;
    try { global.sessionStorage.removeItem(STORAGE_KEY); } catch (_error) {}
    document.querySelectorAll("[data-outfit-pending-input]").forEach((input) => input.remove());
  }
  function contextValue(element, key) {
    let current = element instanceof global.Element ? element : null;
    while (current) {
      if (current.dataset && current.dataset[key]) return current.dataset[key];
      current = current.parentElement;
    }
    return "";
  }
  function contextFromElement(element) {
    const root = element?.closest?.("kixbox-outfit-tabs");
    const selected = root?.querySelector('[data-outfit-tab][aria-selected="true"]');
    return {
      sourceProductId: contextValue(element, "outfitSourceProductId"),
      outfitProductId: contextValue(element, "outfitTargetProductId") || contextValue(element, "outfitProductId"),
      outfitId: contextValue(element, "outfitId") || selected?.dataset.outfitId || "",
      outfitKind: contextValue(element, "outfitKind") || selected?.dataset.outfitKind || "",
      outfitSurface: contextValue(element, "outfitSurface") || "outfit",
      outfitStyle: contextValue(element, "outfitStyle") || selected?.dataset.outfitStyle || "",
    };
  }
  function track(eventName, context, extra) {
    if (!global.KixboxOutfitAnalytics || typeof global.KixboxOutfitAnalytics.track !== "function") return;
    return global.KixboxOutfitAnalytics.track(pageContext()?.ga4Destination, eventName, context, extra);
  }
  function installProperties(form, context) {
    form.querySelectorAll("[data-outfit-pending-input]").forEach((input) => input.remove());
    Object.entries(lineProperties(context)).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.outfitPendingInput = "true";
      form.append(input);
    });
  }
  function applyPendingContext() {
    const page = pageContext();
    const pending = readPendingContext();
    if (!page || !pending || !isPendingContextValid(pending, page.productId)) return;
    state.pending = pending;
    document.querySelectorAll('product-form form[action*="/cart/add"]')
      .forEach((form) => installProperties(form, pending));
  }
  const route = (name, fallback) => global.routes?.[name] || `${global.Shopify?.routes?.root || "/"}${fallback}`;
  const ajaxRoute = (path) => `${global.Shopify?.routes?.root || "/"}${path}`;
  const wait = (milliseconds) => new Promise((resolve) => global.setTimeout(resolve, milliseconds));
  async function updateCartAttributes() {
    for (let attempt = 1; attempt <= MAX_CART_ATTRIBUTE_ATTEMPTS; attempt += 1) {
      try {
        const response = await global.fetch(ajaxRoute("cart/update.js"), {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ attributes: CART_ATTRIBUTES }),
        });
        if (response.ok) return true;
        if (attempt === MAX_CART_ATTRIBUTE_ATTEMPTS || (response.status !== 429 && response.status < 500)) return false;
      } catch (_error) {
        if (attempt === MAX_CART_ATTRIBUTE_ATTEMPTS) return false;
      }
      await wait(250);
    }
    return false;
  }
  function setAddStatus(form, message) {
    const customStatus = form.querySelector("[data-outfit-action-status]");
    const status = customStatus || form.closest("product-form")?.querySelector(".product-form__error-message");
    if (!status) return;
    status.textContent = message;
    if (customStatus) customStatus.hidden = !message;
    else status.closest(".product-form__error-message-wrapper")?.toggleAttribute("hidden", !message);
  }
  const widgetLabel = (form, key) => form.closest("kixbox-outfit-tabs")?.dataset[key] || "";
  async function submitAttributedAdd(form, context = contextFromElement(form)) {
    if (form.dataset.outfitSubmitting === "true") return;
    const button = form.querySelector('[type="submit"]');
    form.dataset.outfitSubmitting = "true";
    form.setAttribute("aria-busy", "true");
    if (button) button.disabled = true;
    setAddStatus(form, "");
    try {
      const formData = new global.FormData(form);
      const item = buildAttributedItem({ variantId: formData.get("id"),
        quantity: formData.get("quantity"), sellingPlan: formData.get("selling_plan") }, context);
      const response = await global.fetch(ajaxRoute("cart/add.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items: [item] }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || widgetLabel(form, "outfitAddError"));
      }
      if (!addedItemHasAttribution(result, item.id, context)) {
        track("outfit_add_to_cart_error", context, { error_stage: "line_properties" });
        clearPendingContext();
        global.location.assign(route("cart_url", "cart"));
        return;
      }
      const cartAttributed = await updateCartAttributes();
      track("outfit_add_to_cart_success", context, {
        outfit_variant_id: item.id,
        cart_attribute_status: cartAttributed ? "applied" : "line_only",
      });
      clearPendingContext();
      setAddStatus(form, widgetLabel(form, "outfitAddedStatus"));
      global.location.assign(route("cart_url", "cart"));
    } catch (error) {
      track("outfit_add_to_cart_error", context, { error_stage: "add" });
      setAddStatus(form, error.message || widgetLabel(form, "outfitAddError"));
      delete form.dataset.outfitSubmitting;
      form.removeAttribute("aria-busy");
      if (button) button.disabled = false;
    }
  }
  async function onNativeCartUpdate(event) {
    if (event.source !== "product-form" || !state.pending) return;
    if (stringValue(event.productVariantId) !== state.submittedVariantId) return;
    const context = state.pending;
    try {
      const response = await global.fetch(ajaxRoute("cart.js"), {
        credentials: "same-origin", headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("cart read failed");
      if (!addedItemHasAttribution(await response.json(), event.productVariantId, context)) {
        throw new Error("line attribution missing");
      }
    } catch (_error) {
      track("outfit_add_to_cart_error", context, { error_stage: "line_properties" });
      return;
    }
    const cartAttributed = await updateCartAttributes();
    track("outfit_add_to_cart_success", context, {
      outfit_variant_id: event.productVariantId,
      cart_attribute_status: cartAttributed ? "applied" : "line_only",
    });
    clearPendingContext();
  }
  function subscribeToNativeCart(attempt = 0) {
    if (state.subscribed) return;
    const subscriber = typeof subscribe === "function" ? subscribe : global.subscribe;
    const events = typeof PUB_SUB_EVENTS !== "undefined"
      ? { cartUpdate: PUB_SUB_EVENTS.cartUpdate, cartError: PUB_SUB_EVENTS.cartError }
      : global.PUB_SUB_EVENTS;
    if (typeof subscriber === "function" && events?.cartUpdate && events?.cartError) {
      subscriber(events.cartUpdate, onNativeCartUpdate);
      subscriber(events.cartError, (event) => {
        if (!state.pending || stringValue(event.productVariantId) !== state.submittedVariantId) return;
        track("outfit_add_to_cart_error", state.pending, { error_stage: "add" });
      });
      state.subscribed = true;
      return;
    }
    if (attempt < 24) global.setTimeout(() => subscribeToNativeCart(attempt + 1), 200);
  }
  document.addEventListener("kixbox:outfit-event", (event) =>
    track(event.detail?.name, contextFromElement(event.detail?.element)));
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof global.Element)) return;
    const similarLink = event.target.closest('[data-outfit-similar-product] a[href*="/products/"]');
    const similar = similarLink?.closest("[data-outfit-similar-product]");
    const direct = event.target.closest("a[data-outfit-product-link]");
    const carrier = similar || direct;
    if (!carrier) return;
    const context = contextFromElement(carrier);
    if (similar) context.outfitSurface = "similar";
    if (!context.outfitProductId) return;
    track("outfit_product_click", context);
    const pending = createPendingContext({ ...context, targetProductId: context.outfitProductId });
    try { global.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending)); } catch (_error) {}
  }, true);
  document.addEventListener("submit", (event) => {
    if (!(event.target instanceof global.HTMLFormElement)) return;
    const form = event.target;
    if (form.matches("[data-outfit-direct-form]")) {
      event.preventDefault();
      submitAttributedAdd(form);
      return;
    }
    if (!form.closest("product-form")) return;
    const page = pageContext();
    const pending = readPendingContext();
    if (!page || !pending || !isPendingContextValid(pending, page.productId)) {
      document.querySelectorAll("[data-outfit-pending-input]").forEach((input) => input.remove());
      return;
    }
    state.pending = pending;
    state.submittedVariantId = stringValue(new global.FormData(form).get("id"));
    installProperties(form, pending);
    const hasCartSurface = Boolean(document.querySelector("cart-notification, cart-drawer"));
    if (shouldInterceptNativeAdd(hasCartSurface, true)) {
      event.preventDefault();
      event.stopPropagation();
      submitAttributedAdd(form, pending);
    }
  }, true);
  applyPendingContext();
  subscribeToNativeCart();
})(typeof window !== "undefined" ? window : globalThis);
